import { useCallback, useState } from 'react';
import { ActionIcon, Badge, Group, Loader, Skeleton, Stack, Table, Text, Title } from '@mantine/core';
import dayjs from 'dayjs';
import { Icon } from '@/components';
import { useCaminoMemberImports } from '@/hooks/organization-programs';
import { caminoService } from '@/services/camino.service';
import {
  CAMINO_MEMBER_IMPORT_STATUS,
  CAMINO_MEMBER_IMPORT_STATUS_LABELS,
  type CaminoMemberImportRun,
} from '@/types';
import styles from './CaminoMemberImportsTable.module.sass';

const LAYOUT = {
  SKELETON_ROWS: 3,
  SKELETON_HEIGHT: 30,
  ACTION_ICON_SIZE: 20,
};

const CONTENT = {
  TITLE: 'Import History',
  EMPTY: 'No imports yet',
  COLUMNS: {
    ID: 'Import ID',
    UPLOADED_AT: 'Uploaded at',
    STATUS: 'Status',
    REPORT: 'Report',
  },
};

const CAMINO_MEMBER_IMPORT_STATUS_COLORS: Record<CAMINO_MEMBER_IMPORT_STATUS, string> = {
  [CAMINO_MEMBER_IMPORT_STATUS.PENDING]: 'yellow',
  [CAMINO_MEMBER_IMPORT_STATUS.PROCESSING]: 'blue',
  [CAMINO_MEMBER_IMPORT_STATUS.COMPLETED]: 'green',
  [CAMINO_MEMBER_IMPORT_STATUS.FAILED]: 'red',
};

type CaminoMemberImportsTableProps = {
  churchId: string;
};

const formatImportDate = (value: string): string => dayjs(value).format('MMM DD, YYYY h:mm A');

const CaminoMemberImportsTable = ({ churchId }: CaminoMemberImportsTableProps) => {
  const { data: imports = [], isLoading, isFetching, isError } = useCaminoMemberImports(churchId);
  const [downloadingImportId, setDownloadingImportId] = useState<string | null>(null);

  const handleDownloadReport = useCallback(
    async (importId: string) => {
      setDownloadingImportId(importId);
      try {
        const blob = await caminoService.getCaminoMemberImportReport(churchId, importId);
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = `camino-member-import-${importId}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(objectUrl);
      } catch (error) {
        console.error('Failed to download Camino member import report:', error);
      } finally {
        setDownloadingImportId(null);
      }
    },
    [churchId]
  );

  const skeletonRows = Array.from({ length: LAYOUT.SKELETON_ROWS }).map((_, index) => (
    <Table.Tr key={`skeleton-${index}`}>
      <Table.Td>
        <Skeleton height={LAYOUT.SKELETON_HEIGHT} width="90%" radius="sm" />
      </Table.Td>
      <Table.Td>
        <Skeleton height={LAYOUT.SKELETON_HEIGHT} width="60%" radius="sm" />
      </Table.Td>
      <Table.Td>
        <Skeleton height={LAYOUT.SKELETON_HEIGHT} width="40%" radius="sm" />
      </Table.Td>
      <Table.Td className={styles.reportCell}>
        <Group justify="center">
          <Skeleton height={LAYOUT.ACTION_ICON_SIZE} width={LAYOUT.ACTION_ICON_SIZE} radius="sm" />
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  const renderStatusBadge = (importRun: CaminoMemberImportRun) => (
    <Badge color={CAMINO_MEMBER_IMPORT_STATUS_COLORS[importRun.status]} variant="light">
      {CAMINO_MEMBER_IMPORT_STATUS_LABELS[importRun.status]}
    </Badge>
  );

  const renderReportAction = (importRun: CaminoMemberImportRun) => {
    if (!importRun.hasReport) {
      return null;
    }

    return (
      <Group gap="xs" justify="center">
        <ActionIcon
          aria-label={`Download Camino member import report for ${importRun.id}`}
          variant="subtle"
          loading={downloadingImportId === importRun.id}
          onClick={() => handleDownloadReport(importRun.id)}
        >
          <Icon name="IconDownload" size={LAYOUT.ACTION_ICON_SIZE} />
        </ActionIcon>
      </Group>
    );
  };

  const rows = imports.map((importRun) => (
    <Table.Tr key={importRun.id}>
      <Table.Td className={styles.importIdCell}>
        <Text size="sm" ff="monospace">
          {importRun.id}
        </Text>
      </Table.Td>
      <Table.Td>{formatImportDate(importRun.createdAt)}</Table.Td>
      <Table.Td>{renderStatusBadge(importRun)}</Table.Td>
      <Table.Td className={styles.reportCell}>{renderReportAction(importRun)}</Table.Td>
    </Table.Tr>
  ));

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <Title order={4}>{CONTENT.TITLE}</Title>
        {isFetching && !isLoading && <Loader size="sm" aria-label="Refreshing import history" />}
      </Group>

      {isError ? (
        <Text c="red" size="sm">
          Failed to load import history
        </Text>
      ) : (
        <Table striped highlightOnHover withRowBorders withColumnBorders withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{CONTENT.COLUMNS.ID}</Table.Th>
              <Table.Th>{CONTENT.COLUMNS.UPLOADED_AT}</Table.Th>
              <Table.Th>{CONTENT.COLUMNS.STATUS}</Table.Th>
              <Table.Th className={styles.reportCell}>{CONTENT.COLUMNS.REPORT}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {isLoading ? skeletonRows : rows}
            {!isLoading && imports.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={4}>
                  <Text c="dimmed">{CONTENT.EMPTY}</Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
};

export default CaminoMemberImportsTable;
